import { useMemo, useState, type ReactNode } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import { Dialog } from '@/components/ui/dialog'
import type { AdminColumn, Fetcher, FetchParams, FetchResult, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { api } from '@/lib/api'

/**
 * Generic admin-registry CRUD table (SecCrudTable / GeoRegistryTable naqshi).
 * "ro'yxat + qo'shish-form + tahrir + o'chirish" — bitta admin-boshqariladigan
 * reyestr uchun (masalan KMS kalitlari, TLS sertifikatlar, push sertifikatlar,
 * TGS validatsiya qoidalari, perceptual-dedup indeksi). Backend REAL:
 * GET/POST/PUT/DELETE `/admin/…`; ro'yxat fail-soft bo'sh qaytishi mumkin,
 * mutatsiya real POST/PUT/DELETE qiladi (soxta ma'lumot yo'q). Ruxsat prop
 * orqali (default `USERS_VIEW` — eski skeleton ekranlar bilan bir xil).
 */

/** Har reyestr qatori `id` bilan keladi (backend beradi). */
export interface RegRow {
  id: number | string
  [key: string]: unknown
}

export type RegFieldType = 'text' | 'number' | 'select' | 'checkbox' | 'date'

export interface RegField {
  /** Yuboriladigan `body` kaliti. */
  name: string
  placeholder: string
  type?: RegFieldType
  mono?: boolean
  /** `select` uchun variantlar. */
  options?: { value: string; label: string }[]
  /** Boshlang'ich qiymat (select/checkbox uchun foydali). */
  defaultValue?: string
}

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi; GEO_STR/SEC_STR kabi). */
export const REG_STR = {
  add: 'Добавить',
  edit: 'Изменить',
  del: 'Удалить',
  save: 'Сохранить',
  cancel: 'Отмена',
  editEntry: 'Редактировать запись',
  created: 'Запись создана',
  updated: 'Запись обновлена',
  deleted: 'Запись удалена',
  failed: 'Не удалось выполнить',
} as const

// ── generic data layer (fail-soft read, typed write) ────────────────────────
function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

function regList<T extends RegRow>(
  path: string,
  key: string,
  searchKeys: string[],
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) =>
    safeList<T>(path, key, params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
}

function regCreate(path: string, body: Record<string, unknown>): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>(path, body)
}

function regUpdate(path: string, id: number | string, body: Record<string, unknown>): Promise<{ ok: boolean }> {
  return api.put<{ ok: boolean }>(`${path}/${encodeURIComponent(String(id))}`, body)
}

function regDelete(path: string, id: number | string): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`${path}/${encodeURIComponent(String(id))}`)
}

export interface RegistryCrudTableProps<T extends RegRow> {
  screenId: string
  title: string
  description?: string
  addLabel: string
  /** Backend yo'li, masalan `/admin/developers/kms-keys`. */
  path: string
  /** Ro'yxat javobidagi massiv kaliti, masalan `keys` / `certs` / `rules`. */
  listKey: string
  /** Tez qidiruvda solishtiriladigan maydonlar. */
  searchKeys: string[]
  fields: RegField[]
  columns: AdminColumn<T>[]
  icon: ReactNode
  /** `canAdd` shu maydon to'ldirilganda faollashadi (default: fields[0]). */
  requiredField?: string
  /** Placeholder ko'rish ruxsati (default USERS_VIEW). */
  permission?: string
}

/** Bo'sh formani maydon defaultlaridan quradi. */
function blankForm(fields: RegField[]): Record<string, string> {
  const f: Record<string, string> = {}
  for (const fld of fields) {
    if (fld.defaultValue !== undefined) f[fld.name] = fld.defaultValue
    else if (fld.type === 'checkbox') f[fld.name] = 'false'
    else if (fld.type === 'select') f[fld.name] = fld.options?.[0]?.value ?? ''
    else f[fld.name] = ''
  }
  return f
}

/** Formani API `body` ga aylantiradi (tur bo'yicha). */
function toBody(fields: RegField[], form: Record<string, string>): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const f of fields) {
    const raw = form[f.name] ?? ''
    if (f.type === 'number') body[f.name] = Number(raw) || 0
    else if (f.type === 'checkbox') body[f.name] = raw === 'true'
    else body[f.name] = raw.trim()
  }
  return body
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: RegField
  value: string
  onChange: (v: string) => void
}) {
  const base =
    'rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong'
  if (field.type === 'checkbox') {
    return (
      <label className="inline-flex items-center gap-2 text-sm text-primary">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          className="h-4 w-4 accent-accent"
        />
        {field.placeholder}
      </label>
    )
  }
  if (field.type === 'select') {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${base} px-2`}
        aria-label={field.placeholder}
      >
        {field.options?.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    )
  }
  return (
    <input
      value={value}
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      spellCheck={false}
      aria-label={field.placeholder}
      className={`min-w-[150px] flex-1 ${base} ${field.mono ? 'font-mono' : ''}`}
    />
  )
}

export function RegistryCrudTable<T extends RegRow>({
  screenId,
  title,
  description,
  addLabel,
  path,
  listKey,
  searchKeys,
  fields,
  columns,
  icon,
  requiredField,
  permission,
}: RegistryCrudTableProps<T>) {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState<Record<string, string>>(() => blankForm(fields))
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<T | null>(null)
  const [editForm, setEditForm] = useState<Record<string, string>>({})

  const reqField = requiredField ?? fields[0]?.name ?? ''
  const canAdd = Boolean(form[reqField]?.trim())
  const reload = () => setReloadKey((k) => k + 1)

  const add = async () => {
    if (!canAdd || busy) return
    setBusy(true)
    try {
      await regCreate(path, toBody(fields, form))
      toast(REG_STR.created, { tone: 'success' })
      setForm(blankForm(fields))
      reload()
    } catch {
      toast(REG_STR.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const openEdit = (row: T) => {
    const f: Record<string, string> = {}
    for (const fld of fields) {
      const v = row[fld.name]
      f[fld.name] =
        fld.type === 'checkbox' ? (v ? 'true' : 'false') : v === undefined || v === null ? '' : String(v)
    }
    setEditForm(f)
    setEditing(row)
  }

  const saveEdit = async () => {
    if (!editing || busy) return
    if (!editForm[reqField]?.trim()) {
      toast(REG_STR.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await regUpdate(path, editing.id, toBody(fields, editForm))
      toast(REG_STR.updated, { tone: 'success' })
      setEditing(null)
      reload()
    } catch {
      toast(REG_STR.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const rowActions = useMemo<RowAction<T>[]>(
    () => [
      {
        id: 'edit',
        label: REG_STR.edit,
        icon: <Pencil size={14} />,
        onSelect: (r) => openEdit(r),
      },
      {
        id: 'delete',
        label: REG_STR.del,
        icon: <Trash2 size={14} />,
        danger: true,
        onSelect: async (r) => {
          try {
            await regDelete(path, r.id)
            toast(REG_STR.deleted, { tone: 'success' })
            reload()
          } catch {
            toast(REG_STR.failed, { tone: 'error' })
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, toast, fields],
  )

  const searchKeyStr = searchKeys.join('|')
  const fetcher = useMemo<Fetcher<T>>(
    () => regList<T>(path, listKey, searchKeyStr.split('|')),
    [path, listKey, searchKeyStr],
  )

  return (
    <>
      <PageHeader title={title} screenId={screenId} description={description} />
      <div className="flex flex-wrap items-center gap-2 p-4 pb-0">
        {fields.map((f) => (
          <FieldInput
            key={f.name}
            field={f}
            value={form[f.name] ?? ''}
            onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))}
          />
        ))}
        <button
          type="button"
          onClick={add}
          disabled={busy || !canAdd}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {addLabel}
        </button>
      </div>

      <DataTable<T>
        key={reloadKey}
        screenId={screenId}
        permission={permission ?? PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon }}
      />

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={REG_STR.editEntry}
        footer={
          <>
            <button
              type="button"
              onClick={saveEdit}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {REG_STR.save}
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="rounded-md border border-border-default px-3 py-1.5 text-sm text-primary hover:bg-surface-1"
            >
              {REG_STR.cancel}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {fields.map((f) => (
            <div key={f.name} className="flex flex-col gap-1">
              <span className="text-xs text-secondary">{f.placeholder}</span>
              <FieldInput
                field={f}
                value={editForm[f.name] ?? ''}
                onChange={(v) => setEditForm((s) => ({ ...s, [f.name]: v }))}
              />
            </div>
          ))}
        </div>
      </Dialog>
    </>
  )
}
